import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Phone, Car, Clock, Navigation } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyB9GtdgSuzSiyvqpIRS-tz9gQa7Af6iTMI';

function LiveTrackingPage() {
  const { bookingId } = useParams();
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);

  useEffect(() => {
    // Load Google Maps script
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Google Maps loaded successfully');
        fetchTrackingData();
      };
      script.onerror = (error) => {
        console.error('Failed to load Google Maps:', error);
        toast.error('Failed to load Google Maps. Please check your internet connection.');
      };
      document.head.appendChild(script);
    } else {
      fetchTrackingData();
    }

    // Poll for updates every 10 seconds
    const interval = setInterval(() => {
      fetchTrackingData();
    }, 10000);

    return () => clearInterval(interval);
  }, [bookingId]);

  const fetchTrackingData = async () => {
    try {
      const response = await axios.get(`${API}/api/tracking/${bookingId}`);
      setTrackingData(response.data);
      setLoading(false);
      
      if (window.google && response.data) {
        updateMap(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch tracking data');
      setLoading(false);
    }
  };

  const updateMap = (data) => {
    if (!mapRef.current || !window.google || !window.google.maps) {
      console.warn('Google Maps not ready yet');
      return;
    }

    try {
      // Initialize map if not already initialized
      if (!googleMapRef.current) {
        const mapOptions = {
          zoom: 13,
          center: data.pickup_location || { lat: 51.5074, lng: -0.1278 },
          mapTypeControl: false,
          fullscreenControl: false,
        };
        googleMapRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
      }
    } catch (error) {
      console.error('Error initializing map:', error);
      toast.error('Error loading map. Please refresh the page.');
      return;
    }

    const map = googleMapRef.current;
    const bounds = new window.google.maps.LatLngBounds();

    // Add/Update pickup marker
    if (data.pickup_location && !pickupMarkerRef.current) {
      pickupMarkerRef.current = new window.google.maps.Marker({
        position: data.pickup_location,
        map: map,
        title: 'Pickup Location',
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
        }
      });
      bounds.extend(data.pickup_location);
    }

    // Add/Update dropoff marker
    if (data.dropoff_location && !dropoffMarkerRef.current) {
      dropoffMarkerRef.current = new window.google.maps.Marker({
        position: data.dropoff_location,
        map: map,
        title: 'Drop-off Location',
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
        }
      });
      bounds.extend(data.dropoff_location);
    }

    // Add/Update driver marker
    if (data.driver && data.driver.current_location) {
      const driverLocation = data.driver.current_location;
      
      if (driverMarkerRef.current) {
        // Update existing marker
        driverMarkerRef.current.setPosition(driverLocation);
      } else {
        // Create new marker
        driverMarkerRef.current = new window.google.maps.Marker({
          position: driverLocation,
          map: map,
          title: `Driver: ${data.driver.name}`,
          icon: {
            url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
            scaledSize: new window.google.maps.Size(40, 40)
          }
        });
      }
      bounds.extend(driverLocation);
    }

    // Fit map to show all markers
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (!trackingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-red-600">Booking not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold">Live Tracking</h1>
          <p className="text-purple-100">Booking #{trackingData.booking_id?.substring(0, 8)}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        {/* Map */}
        <Card className="mb-4">
          <CardContent className="p-0">
            <div ref={mapRef} style={{ width: '100%', height: '400px' }}></div>
          </CardContent>
        </Card>

        {/* Driver Info */}
        {trackingData.driver ? (
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car size={20} />
                Driver Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Driver Name</p>
                  <p className="font-semibold">{trackingData.driver.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-semibold">
                    <a href={`tel:${trackingData.driver.phone}`} className="text-blue-600 hover:underline">
                      <Phone size={14} className="inline mr-1" />
                      {trackingData.driver.phone}
                    </a>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Vehicle</p>
                  <p className="font-semibold">{trackingData.driver.vehicle_type?.toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">License Plate</p>
                  <p className="font-semibold">{trackingData.driver.license_plate}</p>
                </div>
              </div>
              {trackingData.driver.last_location_update && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800">
                    <Navigation size={14} className="inline mr-1" />
                    Last updated: {new Date(trackingData.driver.last_location_update).toLocaleTimeString()}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-4">
            <CardContent className="pt-6">
              <div className="text-center p-8">
                <Car size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Driver not yet assigned</p>
                <p className="text-sm text-gray-500 mt-2">We'll update you once a driver is assigned to your booking</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Booking Details */}
        <Card>
          <CardHeader>
            <CardTitle>Booking Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Badge className="mb-2">{trackingData.status}</Badge>
                <p className="text-sm text-gray-600">Status</p>
              </div>
              
              <div>
                <div className="flex items-start gap-2 mb-3">
                  <MapPin size={20} className="text-green-600 mt-1" />
                  <div>
                    <p className="font-semibold">Pickup Location</p>
                    <p className="text-gray-600">{trackingData.pickup_location?.address}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-2">
                  <MapPin size={20} className="text-red-600 mt-1" />
                  <div>
                    <p className="font-semibold">Drop-off Location</p>
                    <p className="text-gray-600">{trackingData.dropoff_location?.address}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Clock size={20} />
                <div>
                  <p className="font-semibold">{trackingData.booking_date} at {trackingData.booking_time}</p>
                  <p className="text-sm text-gray-600">Scheduled Time</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Auto-refresh notice */}
        <div className="text-center mt-4 text-sm text-gray-500">
          <p>🔄 Location updates automatically every 10 seconds</p>
        </div>
      </div>
    </div>
  );
}

export default LiveTrackingPage;
